// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Contact from "./contact.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "body": {
      "column": {
        "default": null,
        "name": "body",
        "null": false,
        "type": "text"
      },
      "name": "body",
      "selected_by_default": null,
      "translated": null
    },
    "contact_id": {
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": null
      },
      "name": "contact_id",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "dialogue_type": {
      "column": {
        "default": null,
        "name": "dialogue_type",
        "null": false,
        "type": "string"
      },
      "name": "dialogue_type",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "translated_dialogue_type": {
      "column": null,
      "name": "translated_dialogue_type",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Dialogue",
  "collectionKey": "dialogues",
  "collectionName": "dialogues",
  "i18nKey": "dialogue",
  "camelizedLower": "dialogue",
  "name": "Dialogue",
  "nameDasherized": "dialogue",
  "pluralName": "dialogues",
  "ransackable_associations": [
    {
      "className": "Dialogue::Audit",
      "collectionName": "dialogue_audits",
      "foreignKey": "dialogue_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "DialogueAudit",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Dialogue::Audit",
      "collectionName": "dialogue_audits",
      "foreignKey": "dialogue_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "DialogueAudit",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "contact_id",
      "name": "user",
      "macro": "has_one",
      "resource_name": "User",
      "through": "contact"
    }
  ],
  "ransackable_attributes": [
    {
      "name": "body",
      "column": {
        "default": null,
        "name": "body",
        "null": false,
        "type": "text"
      }
    },
    {
      "name": "contact_id",
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "dialogue_type",
      "column": {
        "default": null,
        "name": "dialogue_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "contact_id",
      "name": "user",
      "macro": "has_one",
      "resource_name": "User",
      "through": "contact"
    }
  ],
  "paramKey": "dialogue",
  "primaryKey": "id"
}

/** Frontend model for Dialogue. */
export default class Dialogue extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  body() {
    return this.readAttributeUnderscore("body")
  }

  /** @returns {boolean} */
  hasBody() {
    const value = this.body()

    return this._isPresent(value)
  }

  /** @returns {any} */
  contactId() {
    return this.readAttributeUnderscore("contact_id")
  }

  /** @returns {boolean} */
  hasContactId() {
    const value = this.contactId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  dialogueType() {
    return this.readAttributeUnderscore("dialogue_type")
  }

  /** @returns {boolean} */
  hasDialogueType() {
    const value = this.dialogueType()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  translatedDialogueType() {
    return this.readAttributeUnderscore("translated_dialogue_type")
  }

  /** @returns {boolean} */
  hasTranslatedDialogueType() {
    const value = this.translatedDialogueType()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./contact.js").default | null} */
  contact() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "contact"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadContact() {
    if (!("contactId" in this)) throw new Error("Foreign key method wasn't defined: contactId")

    const id = this.contactId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  ensureContactLoaded() {
    return this.ensureAssociationLoaded("contact")
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readHasOneReflection({modelClass: User, reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "user",
        model: this,
        modelClass: User
      },
      {
        params: {
          through: {
            model: "Dialogue",
            id,
            reflection: "user"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  ensureUserLoaded() {
    return this.ensureAssociationLoaded("user")
  }
}
